/**
 * Depth Anything V2 Small pre/post-processing, reproducing the iOS pipeline (docs/spec/05 §2)
 * so the shader thresholds tuned on it (sky 0.04/0.12, sigma 0.12) stay valid. Pure.
 */
export const MODEL_W = 518;
export const MODEL_H = 392;
export const OUTPUT_LONG_SIDE = 512;
export const MAX_STRETCH = 1.35;
export const IMAGENET_MEAN = [0.485, 0.456, 0.406] as const;
export const IMAGENET_STD = [0.229, 0.224, 0.225] as const;
/** Cache key suffix (iOS: '<hash32>-DepthAnythingV2SmallF16-p2-512'). */
export const DEPTH_PIPELINE = 'DepthAnythingV2Small-web-p2-512';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Swift `.rounded()`: half away from zero. */
export function roundHalfAway(v: number): number {
  return Math.sign(v) * Math.round(Math.abs(v));
}

/** Where the photo goes in the 518×392 input: stretched unless the aspect differs by > 1.35×. */
export function contentRect(imageAspect: number, w = MODEL_W, h = MODEL_H): Rect {
  const modelAspect = w / h;
  let cw = w;
  let ch = h;
  if (modelAspect / imageAspect > MAX_STRETCH) cw = Math.min(w, roundHalfAway(h * imageAspect * MAX_STRETCH));
  else if (imageAspect / modelAspect > MAX_STRETCH) ch = Math.min(h, roundHalfAway(w / (imageAspect / MAX_STRETCH)));
  return { x: roundHalfAway((w - cw) / 2), y: roundHalfAway((h - ch) / 2), width: cw, height: ch };
}

/** Neutral pad color (ImageNet mean, 0..255). */
export const PAD_RGB = IMAGENET_MEAN.map((m) => m * 255) as unknown as [number, number, number];

/** RGBA8 (MODEL_W × MODEL_H, already padded + drawn) → NCHW float32 ImageNet-normalized. */
export function toInputTensor(rgba: Uint8ClampedArray | Uint8Array, w = MODEL_W, h = MODEL_H): Float32Array {
  const n = w * h;
  const out = new Float32Array(3 * n);
  for (let i = 0; i < n; i++) {
    for (let c = 0; c < 3; c++) out[c * n + i] = (rgba[i * 4 + c] / 255 - IMAGENET_MEAN[c]) / IMAGENET_STD[c];
  }
  return out;
}

/** In-model postprocessing of the Core ML conversion: relu, then divide by the global max. */
export function reluNormalize(values: Float32Array): Float32Array {
  let max = 0;
  for (const v of values) if (Number.isFinite(v) && v > max) max = v;
  const out = new Float32Array(values.length);
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    out[i] = Number.isFinite(v) ? (max > 0 ? Math.max(0, v) / max : 0) : v;
  }
  return out;
}

/** Crop of the content region from a raw output of size rawW × rawH. */
export function cropRect(content: Rect, rawW: number, rawH: number, w = MODEL_W, h = MODEL_H): Rect {
  const sx = rawW / w;
  const sy = rawH / h;
  const x = Math.max(0, roundHalfAway(content.x * sx));
  const y = Math.max(0, roundHalfAway(content.y * sy));
  return {
    x,
    y,
    width: Math.min(rawW - x, Math.max(1, roundHalfAway(content.width * sx))),
    height: Math.min(rawH - y, Math.max(1, roundHalfAway(content.height * sy))),
  };
}

/** Output size: photo aspect, long side 512. */
export function outputSize(aspect: number, longSide = OUTPUT_LONG_SIDE): [number, number] {
  const L = Math.max(8, longSide);
  return aspect >= 1 ? [L, Math.max(1, Math.round(L / aspect))] : [Math.max(1, Math.round(L * aspect)), L];
}

function lanczos3(x: number): number {
  if (x === 0) return 1;
  if (x <= -3 || x >= 3) return 0;
  const px = Math.PI * x;
  return (3 * Math.sin(px) * Math.sin(px / 3)) / (px * px);
}

/** 1-D Lanczos-3 resample of `rows` lines of length srcLen (stride 1 within a line). */
function resample1d(
  src: Float32Array,
  srcLen: number,
  lines: number,
  dstLen: number,
  transpose: boolean,
): Float32Array {
  const out = new Float32Array(dstLen * lines);
  const scale = srcLen / dstLen;
  const support = 3 * Math.max(1, scale);
  for (let o = 0; o < dstLen; o++) {
    const center = (o + 0.5) * scale - 0.5;
    const lo = Math.max(0, Math.floor(center - support));
    const hi = Math.min(srcLen - 1, Math.ceil(center + support));
    const weights: number[] = [];
    let sum = 0;
    for (let i = lo; i <= hi; i++) {
      const w = lanczos3((i - center) / Math.max(1, scale));
      weights.push(w);
      sum += w;
    }
    for (let l = 0; l < lines; l++) {
      let acc = 0;
      for (let i = lo; i <= hi; i++) {
        const v = transpose ? src[i * lines + l] : src[l * srcLen + i];
        acc += v * weights[i - lo];
      }
      if (transpose) out[o * lines + l] = acc / sum;
      else out[l * dstLen + o] = acc / sum;
    }
  }
  return out;
}

/** High-quality (Lanczos-3, separable) resample of a crop to dstW × dstH. */
export function resampleCrop(src: Float32Array, srcW: number, crop: Rect, dstW: number, dstH: number): Float32Array {
  const c = new Float32Array(crop.width * crop.height);
  for (let y = 0; y < crop.height; y++) {
    for (let x = 0; x < crop.width; x++) c[y * crop.width + x] = src[(crop.y + y) * srcW + crop.x + x];
  }
  const horiz = resample1d(c, crop.width, crop.height, dstW, false); // dstW × crop.height
  return resample1d(horiz, crop.height, dstW, dstH, true); // dstW × dstH
}

/** Robust 2–98 % percentile normalization to 8-bit, white = near (spec `normalize`). */
export function normalizeToBytes(values: Float32Array): Uint8Array {
  const finite: number[] = [];
  for (const v of values) if (Number.isFinite(v)) finite.push(v);
  if (finite.length <= values.length / 2) throw new Error('Depth inference failed: prediction is mostly non-finite');
  finite.sort((a, b) => a - b);
  const last = finite.length - 1;
  let lo = finite[Math.round(last * 0.02)];
  let hi = finite[Math.round(last * 0.98)];
  if (hi - lo < 1e-6) {
    lo = finite[0];
    hi = finite[last];
  }
  if (hi - lo <= 1e-6) throw new Error('Depth inference failed: prediction is constant');
  const out = new Uint8Array(values.length);
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    out[i] = Number.isFinite(v) ? Math.round(Math.max(0, Math.min(1, (v - lo) / (hi - lo))) * 255) : 0;
  }
  return out;
}

/** Full post-processing from the raw model output to the 8-bit map. */
export function postprocess(raw: Float32Array, rawW: number, rawH: number, imageAspect: number) {
  const content = contentRect(imageAspect);
  const crop = cropRect(content, rawW, rawH);
  const [outW, outH] = outputSize(imageAspect);
  const resized = resampleCrop(reluNormalize(raw), rawW, crop, outW, outH);
  return { width: outW, height: outH, data: normalizeToBytes(resized) };
}

/* ------------------------------------------------------------- cache file format */

const MAGIC = 0x31444c42; // 'BLD1'

/** 12-byte header (magic, width, height; little endian) + gray bytes. */
export function encodeDepthFile(width: number, height: number, data: Uint8Array): Uint8Array {
  const out = new Uint8Array(12 + data.length);
  const v = new DataView(out.buffer);
  v.setUint32(0, MAGIC, true);
  v.setUint32(4, width, true);
  v.setUint32(8, height, true);
  out.set(data, 12);
  return out;
}

export function decodeDepthFile(bytes: Uint8Array): { width: number; height: number; data: Uint8Array } | null {
  if (bytes.length < 12) return null;
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (v.getUint32(0, true) !== MAGIC) return null;
  const width = v.getUint32(4, true);
  const height = v.getUint32(8, true);
  if (bytes.length !== 12 + width * height) return null;
  return { width, height, data: bytes.slice(12) };
}
